import React, { memo } from 'react';
import { Handle, Position, NodeProps, Node } from '@xyflow/react';
import { StepNodeData, StepType } from '../types';

/**
 * ГОСТ 19.701-90 / ISO 5807 — стандартные фигуры блок-схем:
 *
 * start     → маленький круг, зелёная заливка (терминатор старт)
 * end       → маленький круг, красная заливка (терминатор конец)
 * action    → прямоугольник (процесс)
 * decision  → ромб (решение) — вход сверху, выходы по бокам
 * switch    → шестиугольник (множественный выбор)
 * subprocess→ прямоугольник с двойными вертикальными линиями
 * data      → параллелограмм (данные)
 * io        → параллелограмм (ввод/вывод)
 * error     → прямоугольник с красной полосой слева
 * comment   → скобка с текстом (аннотация)
 */

const HANDLE_STYLE: React.CSSProperties = {
  background: '#888',
  width: 6,
  height: 6,
  border: '1px solid #fff',
};

/* ─── Scenario color indicator ─── */
const ScenarioDot: React.FC<{ color: string }> = ({ color }) => (
  <div
    style={{
      position: 'absolute',
      top: -4,
      right: -4,
      width: 10,
      height: 10,
      borderRadius: '50%',
      background: color,
      border: '1px solid #fff',
      zIndex: 2,
    }}
  />
);

/* ─── Терминатор СТАРТ — маленький круг зелёный ─── */
const StartShape: React.FC<ShapeProps> = ({ label, borderColor, selected, description, scenarioColor }) => (
  <div
    style={{
      width: 36, height: 36, borderRadius: '50%',
      background: '#4caf50',
      border: `2px solid ${borderColor}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: selected ? `0 0 0 2px ${borderColor}` : '0 1px 3px rgba(0,0,0,0.12)',
      cursor: 'grab', position: 'relative',
    }}
    title={description || label}
  >
    <Handle type="target" position={Position.Top} id="top-in" style={HANDLE_STYLE} />
    <span style={{ fontSize: 8, color: '#fff', fontWeight: 700, textAlign: 'center', lineHeight: 1 }}>
      {label.length > 6 ? '▶' : label}
    </span>
    <Handle type="source" position={Position.Bottom} id="bottom-out" style={HANDLE_STYLE} />
    <Handle type="source" position={Position.Right} id="right-out" style={HANDLE_STYLE} />
    <ScenarioDot color={scenarioColor} />
  </div>
);

/* ─── Терминатор КОНЕЦ — маленький круг красный ─── */
const EndShape: React.FC<ShapeProps> = ({ label, borderColor, selected, description, scenarioColor }) => (
  <div
    style={{
      width: 36, height: 36, borderRadius: '50%',
      background: '#f44336',
      border: `2px solid ${borderColor}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: selected ? `0 0 0 2px ${borderColor}` : '0 1px 3px rgba(0,0,0,0.12)',
      cursor: 'grab', position: 'relative',
    }}
    title={description || label}
  >
    <Handle type="target" position={Position.Top} id="top-in" style={HANDLE_STYLE} />
    <Handle type="target" position={Position.Left} id="left-in" style={HANDLE_STYLE} />
    <span style={{ fontSize: 8, color: '#fff', fontWeight: 700, textAlign: 'center', lineHeight: 1 }}>
      {label.length > 6 ? '■' : label}
    </span>
    <Handle type="source" position={Position.Bottom} id="bottom-out" style={HANDLE_STYLE} />
    <Handle type="source" position={Position.Right} id="right-out" style={HANDLE_STYLE} />
    <ScenarioDot color={scenarioColor} />
  </div>
);

/* ─── Прямоугольник (action / процесс) ─── */
const ActionShape: React.FC<ShapeProps> = ({ label, bg, borderColor, selected, description, scenarioColor }) => (
  <div
    style={{
      minWidth: 140, height: 44, borderRadius: 4,
      background: bg,
      border: `2px solid ${borderColor}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '4px 10px',
      boxShadow: selected ? `0 0 0 2px ${borderColor}` : '0 1px 3px rgba(0,0,0,0.12)',
      cursor: 'grab', position: 'relative',
    }}
    title={description || label}
  >
    <Handle type="target" position={Position.Top} id="top-in" style={HANDLE_STYLE} />
    <Handle type="target" position={Position.Left} id="left-in" style={HANDLE_STYLE} />
    <span style={{ fontSize: 11, color: '#333', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>
      {label}
    </span>
    <Handle type="source" position={Position.Bottom} id="bottom-out" style={HANDLE_STYLE} />
    <Handle type="source" position={Position.Right} id="right-out" style={HANDLE_STYLE} />
    <ScenarioDot color={scenarioColor} />
  </div>
);

/* ─── Ромб (decision / решение) ─── */
const DecisionShape: React.FC<ShapeProps> = ({ label, borderColor, selected, description, scenarioColor }) => (
  <div
    style={{
      width: 90, height: 90, position: 'relative', cursor: 'grab',
    }}
    title={description || label}
  >
    <div
      style={{
        position: 'absolute', top: '50%', left: '50%',
        width: 64, height: 64,
        transform: 'translate(-50%, -50%) rotate(45deg)',
        background: '#eeeeee',
        border: `2px solid ${borderColor}`,
        boxShadow: selected ? `0 0 0 2px ${borderColor}` : '0 1px 3px rgba(0,0,0,0.12)',
      }}
    />
    <div
      style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: 9, fontWeight: 600, color: '#333',
        textAlign: 'center', lineHeight: 1.1,
        width: 70, zIndex: 1, pointerEvents: 'none',
      }}
    >
      {label}
    </div>
    {/* Вход сверху */}
    <Handle type="target" position={Position.Top} id="top-in" style={{ ...HANDLE_STYLE, top: 0 }} />
    {/* Выход Да — справа */}
    <Handle type="source" position={Position.Right} id="right-out" style={{ ...HANDLE_STYLE, right: 0 }} />
    {/* Выход Нет — слева */}
    <Handle type="source" position={Position.Left} id="left-out" style={{ ...HANDLE_STYLE, left: 0 }} />
    {/* Выход вниз (основной поток) */}
    <Handle type="source" position={Position.Bottom} id="bottom-out" style={{ ...HANDLE_STYLE, bottom: 0 }} />
    {/* Вход слева (для обратных связей) */}
    <Handle type="target" position={Position.Left} id="left-in" style={{ ...HANDLE_STYLE, left: 0, top: '60%' }} />
    <ScenarioDot color={scenarioColor} />
  </div>
);

/* ─── Шестиугольник (switch / множественный выбор) ─── */
const SwitchShape: React.FC<ShapeProps> = ({ label, borderColor, selected, description, scenarioColor }) => {
  const w = 120, h = 60;
  const inset = 20;
  const points = `${inset},0 ${w - inset},0 ${w},${h / 2} ${w - inset},${h} ${inset},${h} 0,${h / 2}`;
  return (
    <div style={{ width: w, height: h, position: 'relative', cursor: 'grab' }} title={description || label}>
      <svg width={w} height={h} style={{ position: 'absolute', top: 0, left: 0 }}>
        <polygon
          points={points}
          fill="#e0e0e0"
          stroke={borderColor}
          strokeWidth={2}
          filter={selected ? undefined : 'drop-shadow(0 1px 2px rgba(0,0,0,0.12))'}
        />
      </svg>
      {selected && (
        <div style={{ position: 'absolute', inset: -2, border: `2px solid ${borderColor}`, borderRadius: 4, pointerEvents: 'none' }} />
      )}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: 10, fontWeight: 600, color: '#333',
        textAlign: 'center', lineHeight: 1.1,
        width: w - 30, zIndex: 1, pointerEvents: 'none',
      }}>
        {label}
      </div>
      <Handle type="target" position={Position.Top} id="top-in" style={HANDLE_STYLE} />
      <Handle type="source" position={Position.Right} id="right-out" style={HANDLE_STYLE} />
      <Handle type="source" position={Position.Left} id="left-out" style={HANDLE_STYLE} />
      <Handle type="source" position={Position.Bottom} id="bottom-out" style={HANDLE_STYLE} />
      <Handle type="target" position={Position.Left} id="left-in" style={{ ...HANDLE_STYLE, top: '60%' }} />
      <ScenarioDot color={scenarioColor} />
    </div>
  );
};

/* ─── Подпроцесс (subprocess) — двойные вертикальные линии ─── */
const SubprocessShape: React.FC<ShapeProps> = ({ label, bg, borderColor, selected, description, scenarioColor }) => (
  <div
    style={{
      minWidth: 140, height: 44, borderRadius: 4,
      background: bg || '#e8eaf6',
      border: `2px solid ${borderColor}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '4px 18px',
      boxShadow: selected ? `0 0 0 2px ${borderColor}` : '0 1px 3px rgba(0,0,0,0.12)',
      cursor: 'grab', position: 'relative',
    }}
    title={description || label}
  >
    <div style={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, background: borderColor }} />
    <div style={{ position: 'absolute', right: 8, top: 0, bottom: 0, width: 2, background: borderColor }} />
    <Handle type="target" position={Position.Top} id="top-in" style={HANDLE_STYLE} />
    <Handle type="target" position={Position.Left} id="left-in" style={HANDLE_STYLE} />
    <span style={{ fontSize: 11, color: '#333', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 100, zIndex: 1 }}>
      {label}
    </span>
    <Handle type="source" position={Position.Bottom} id="bottom-out" style={HANDLE_STYLE} />
    <Handle type="source" position={Position.Right} id="right-out" style={HANDLE_STYLE} />
    <ScenarioDot color={scenarioColor} />
  </div>
);

/* ─── Параллелограмм (data / io) ─── */
const ParallelogramShape: React.FC<ShapeProps> = ({ label, bg, borderColor, selected, description, scenarioColor }) => {
  const w = 140, h = 44;
  const skew = 15;
  const points = `${skew},0 ${w},0 ${w - skew},${h} 0,${h}`;
  return (
    <div style={{ width: w, height: h, position: 'relative', cursor: 'grab' }} title={description || label}>
      <svg width={w} height={h} style={{ position: 'absolute', top: 0, left: 0 }}>
        <polygon
          points={points}
          fill={bg || '#ffffff'}
          stroke={borderColor}
          strokeWidth={2}
          filter={selected ? undefined : 'drop-shadow(0 1px 2px rgba(0,0,0,0.12))'}
        />
      </svg>
      {selected && (
        <div style={{ position: 'absolute', inset: -2, border: `2px solid ${borderColor}`, borderRadius: 4, pointerEvents: 'none' }} />
      )}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: 11, color: '#333', lineHeight: 1.2,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        maxWidth: w - 30, zIndex: 1, pointerEvents: 'none',
      }}>
        {label}
      </div>
      <Handle type="target" position={Position.Top} id="top-in" style={HANDLE_STYLE} />
      <Handle type="target" position={Position.Left} id="left-in" style={HANDLE_STYLE} />
      <Handle type="source" position={Position.Bottom} id="bottom-out" style={HANDLE_STYLE} />
      <Handle type="source" position={Position.Right} id="right-out" style={HANDLE_STYLE} />
      <ScenarioDot color={scenarioColor} />
    </div>
  );
};

/* ─── Ошибка (error) — красная полоса слева ─── */
const ErrorShape: React.FC<ShapeProps> = ({ label, bg, borderColor, selected, description, scenarioColor }) => (
  <div
    style={{
      minWidth: 140, height: 44, borderRadius: 4,
      background: bg || '#ffebee',
      border: '2px solid #e53935',
      borderLeft: '6px solid #e53935',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '4px 10px',
      boxShadow: selected ? `0 0 0 2px ${borderColor}` : '0 1px 3px rgba(0,0,0,0.12)',
      cursor: 'grab', position: 'relative',
    }}
    title={description || label}
  >
    <Handle type="target" position={Position.Top} id="top-in" style={HANDLE_STYLE} />
    <Handle type="target" position={Position.Left} id="left-in" style={HANDLE_STYLE} />
    <span style={{ fontSize: 11, color: '#c62828', fontWeight: 500, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>
      {label}
    </span>
    <Handle type="source" position={Position.Bottom} id="bottom-out" style={HANDLE_STYLE} />
    <Handle type="source" position={Position.Right} id="right-out" style={HANDLE_STYLE} />
    <ScenarioDot color={scenarioColor} />
  </div>
);

/* ─── Комментарий (comment) — скобка с текстом ─── */
const CommentShape: React.FC<ShapeProps> = ({ label, borderColor, description }) => (
  <div
    style={{
      minWidth: 100, minHeight: 30,
      borderLeft: `3px solid ${borderColor}`,
      padding: '4px 8px',
      background: '#fffde7',
      cursor: 'grab', position: 'relative',
    }}
    title={description || label}
  >
    <Handle type="target" position={Position.Left} id="left-in" style={HANDLE_STYLE} />
    <span style={{ fontSize: 10, color: '#666', fontStyle: 'italic', lineHeight: 1.2 }}>
      {label}
    </span>
  </div>
);

/* ─── Схлопнутый сценарий (collapsed) — компактная заглушка ─── */
const CollapsedShape: React.FC<ShapeProps> = ({ label, borderColor, selected, description, scenarioColor }) => (
  <div
    style={{
      minWidth: 160, height: 36, borderRadius: 18,
      background: '#f5f5f5',
      border: `2px dashed ${borderColor}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '4px 16px', gap: 6,
      boxShadow: selected ? `0 0 0 2px ${borderColor}` : '0 1px 2px rgba(0,0,0,0.08)',
      cursor: 'pointer', position: 'relative',
      opacity: 0.7,
    }}
    title={description || `Сценарий «${label}» свёрнут. Нажмите для разворачивания.`}
  >
    <Handle type="target" position={Position.Top} id="top-in" style={HANDLE_STYLE} />
    <Handle type="target" position={Position.Left} id="left-in" style={HANDLE_STYLE} />
    <span style={{ fontSize: 10, color: '#999', marginRight: 4 }}>▶▶</span>
    <span style={{ fontSize: 10, color: '#666', fontWeight: 500, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>
      {label}
    </span>
    <Handle type="source" position={Position.Bottom} id="bottom-out" style={HANDLE_STYLE} />
    <Handle type="source" position={Position.Right} id="right-out" style={HANDLE_STYLE} />
    <ScenarioDot color={scenarioColor} />
  </div>
);

/* ─── Shape props interface ─── */
interface ShapeProps {
  label: string;
  bg: string;
  borderColor: string;
  selected: boolean;
  description?: string;
  scenarioColor: string;
}

/* ─── Background colors by type ─── */
const TYPE_BG: Record<StepType, string> = {
  start:      '#4caf50',
  end:        '#f44336',
  action:     '#ffffff',
  decision:   '#eeeeee',
  switch:     '#e0e0e0',
  subprocess: '#e8eaf6',
  data:       '#ffffff',
  io:         '#ffffff',
  error:      '#ffebee',
  comment:    '#fffde7',
  collapsed:  '#f5f5f5',
};

/* ─── Main StepNode ─── */
const StepNode: React.FC<NodeProps<Node<StepNodeData>>> = ({ data, selected }) => {
  const { label, stepType, scenarioColor, laneColor, description } = data;
  const bg = (stepType === 'start' || stepType === 'end')
    ? TYPE_BG[stepType]
    : laneColor || TYPE_BG[stepType];
  const borderColor = scenarioColor;

  const props: ShapeProps = { label, bg, borderColor, selected: !!selected, description, scenarioColor };

  switch (stepType) {
    case 'start':
      return <StartShape {...props} />;
    case 'end':
      return <EndShape {...props} />;
    case 'decision':
      return <DecisionShape {...props} />;
    case 'switch':
      return <SwitchShape {...props} />;
    case 'subprocess':
      return <SubprocessShape {...props} />;
    case 'data':
    case 'io':
      return <ParallelogramShape {...props} />;
    case 'error':
      return <ErrorShape {...props} />;
    case 'comment':
      return <CommentShape {...props} />;
    case 'collapsed':
      return <CollapsedShape {...props} />;
    default:
      return <ActionShape {...props} />;
  }
};

export default memo(StepNode);
